/**
 * Подключение подписки: ссылка для VPN-клиента.
 *
 * Ссылку выдаёт панель Remnawave вскоре после оплаты (задача в очереди),
 * поэтому до её появления показывается «готовим доступ» с повтором запроса.
 */
import { hapticFeedback, openLink } from '@telegram-apps/sdk-react';
import { useState } from 'react';

import { Button } from '@/shared/ui/kit/Button';
import { openExternal } from '@/shared/lib/links';
import { Icon } from '@/shared/ui/kit/Icon';

import styles from './ConnectBlock.module.css';

/** Глобальная версия Happ: в российском App Store её нет. */
const HAPP_APP_STORE = 'https://apps.apple.com/app/happ-proxy-utility/id6504287215';

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Старые WebView без Clipboard API — копирование через временное поле.
    const field = document.createElement('textarea');
    field.value = text;
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    field.remove();
    return ok;
  }
}

export function ConnectBlock({
  url,
  onRefresh,
}: {
  url: string | null | undefined;
  onRefresh: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!url) {
    return (
      <>
        <Button block variant="quiet" onClick={onRefresh}>
          <Icon name="download" size={18} />
          Готовим доступ… обновить
        </Button>
        <p className={styles.hint}>Обычно это занимает до минуты после оплаты.</p>
      </>
    );
  }

  // Telegram открывает из Mini App только https, поэтому happ://add/ —
  // через страницу-переходник на нашем домене (public/open.html).
  const openInHapp = () => {
    const page = `${window.location.origin}/open.html?to=${encodeURIComponent(`happ://add/${url}`)}`;
    if (openLink.isAvailable()) openLink(page);
    else window.open(page, '_blank');
  };

  const onCopy = async () => {
    const ok = await copy(url);
    setCopied(ok);
    if (ok && hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred('success');
    }
    window.setTimeout(() => setCopied(false), 2500);
  };

  return (
    <>
      <Button block onClick={openInHapp}>
        <Icon name="download" size={18} />
        Добавить в Happ
      </Button>
      <Button block variant="quiet" onClick={() => void onCopy()}>
        {copied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
      </Button>
      <p className={styles.hint}>
        Нет Happ? Установите его из App Store или Google Play, затем нажмите кнопку ещё раз. В другом
        приложении: скопируйте ссылку и добавьте подписку из буфера обмена.
      </p>
      <p className={styles.hint}>
        Не работает сервер «Белый Список» на iPhone? Обновите Happ до последней версии. В российском
        App Store её нет: смените регион Apple ID на другую страну и установите{' '}
        <a
          className={styles.link}
          href={HAPP_APP_STORE}
          onClick={(event) => {
            event.preventDefault();
            openExternal(HAPP_APP_STORE);
          }}
        >
          Happ из App Store
        </a>
        .
      </p>
    </>
  );
}
